import { useEffect, useMemo, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';

// Type-ahead vendor picker: suggestions by name / code, selecting calls onSelect(vendor|null)
export const VendorSuggestInput = ({ vendors, value, onSelect, placeholder = 'Type vendor name or code...', testId = 'vendor-suggest' }) => {
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrapRef = useRef(null);

  useEffect(() => {
    setText(value ? `${value.name}${value.code ? ` (${value.code})` : ''}` : '');
  }, [value]);

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const matches = useMemo(() => {
    const q = text.trim().toLowerCase();
    if (!q) return vendors.slice(0, 8);
    return vendors
      .filter(v => (v.name || '').toLowerCase().includes(q) || (v.code || '').toLowerCase().includes(q))
      .slice(0, 8);
  }, [text, vendors]);

  const choose = (v) => { onSelect(v); setOpen(false); };
  const clear = () => { setText(''); onSelect(null); setOpen(false); };

  const onKeyDown = (e) => {
    if (!open) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(a + 1, matches.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (matches[active]) choose(matches[active]); }
    else if (e.key === 'Escape') setOpen(false);
  };

  return (
    <div className="relative w-full" ref={wrapRef}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
      <Input
        value={text}
        placeholder={placeholder}
        onChange={(e) => { setText(e.target.value); setOpen(true); setActive(0); if (value) onSelect(null); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        className="pl-10 pr-8 bg-white border-gray-300"
        data-testid={`${testId}-input`}
        autoComplete="off"
      />
      {text && (
        <button type="button" onClick={clear} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700" data-testid={`${testId}-clear`} aria-label="Clear vendor">
          <X className="w-4 h-4" />
        </button>
      )}
      {open && matches.length > 0 && (
        <ul className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-72 overflow-y-auto text-sm" data-testid={`${testId}-list`}>
          {matches.map((v, i) => (
            <li
              key={v.id}
              onMouseDown={(e) => { e.preventDefault(); choose(v); }}
              onMouseEnter={() => setActive(i)}
              className={`px-3 py-2 cursor-pointer flex items-center justify-between ${i === active ? 'bg-teal-50' : 'hover:bg-gray-50'}`}
              data-testid={`${testId}-option-${v.code || v.id}`}
            >
              <span className="text-gray-800">{v.name}</span>
              <span className="text-xs text-teal-600 font-mono">{v.code}</span>
            </li>
          ))}
        </ul>
      )}
      {open && text.trim() && matches.length === 0 && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg px-3 py-2 text-sm text-gray-500">No vendor matches &quot;{text.trim()}&quot;</div>
      )}
    </div>
  );
};
